import {
  Dictionary,
  LoanDocument,
  StatusData,
  UploadType,
  SolarDocStatus,
  errorToString,
  ChecklistOwner,
} from "@oneethos/shared"
import { useEffect, useRef, useState } from "react"
import { useAppState } from "../../hooks"
import api from '../../api-client'
import { toast } from "react-toastify"
import { UploadSlot } from "./upload-slot"

type FormState = {
  uploadType: UploadType
  stepDocs: Dictionary<LoanDocument & File>
}

type InstallerUploadFormProps = {
  project: StatusData
  onProjectChange: (p: StatusData) => void
  checklistItems?: any[]
  limitToSlots?: string[]
  bankerComments: string
  handleRowToggle?: () => void,
  isCompleted?: boolean,
  dateField?: string
  status?: string
  checklistOwner: ChecklistOwner
}

const usePollForAgentRuns = (
  project: StatusData,
  onChange: (project: StatusData) => void
) => {
  const pollingInterval = useRef(null)
  const runs = project.activeAgentRuns()
  const activeCount = useRef(runs.length)

  const fetchData = () => {
    api.getProject(project.lookup).then(data => {
      onChange(data)
    }).catch(ex => toast.error(errorToString(ex), { autoClose: false }))
  }

  useEffect(() => {
    const runs = project.activeAgentRuns()

    if (activeCount.current > 0 && runs.length === 0) {
      toast.dismiss()
      toast.info('AI review complete, see Checklist for results', { autoClose: false })
      activeCount.current = 0
    }

    activeCount.current = runs.length

    if (runs.length > 0 && !pollingInterval.current) {
      pollingInterval.current = setInterval(fetchData, 3_000)
    } else if (runs.length === 0 && pollingInterval.current) {
      clearInterval(pollingInterval.current)
      pollingInterval.current = null
    }

    return () => {
      clearInterval(pollingInterval.current)
      pollingInterval.current = null
    }
  }, [runs.length])

  return runs
}

export const InstallerUploadForm = ({
  project,
  onProjectChange,
  checklistItems = [],
  limitToSlots,
  handleRowToggle,
  bankerComments,
  isCompleted,
  dateField,
  status,
  checklistOwner
}: InstallerUploadFormProps) => {
  const [, setSubmitting] = useState(false)
  const { installersStatusConfig: isc } = useAppState()
  const step = isc.dict[project.installersStatus]
  const [formState, setFormState] = useState<FormState>({
    uploadType: step?.uploadType,
    stepDocs: (project.installerDocs[step?.uploadType] || {}) as unknown as Dictionary<LoanDocument & File>
  })

  usePollForAgentRuns(project, onProjectChange)

  useEffect(() => {
    const _step = isc.dict[project.installersStatus]

    // this sequencing finds docs uploaded to a slot regardless of which step it was uploaded
    const projectDocs: Dictionary<LoanDocument & File> = {} as unknown as Dictionary<LoanDocument & File>
    Object.values(project.installerDocs).forEach(docMap => {
      Object.assign(projectDocs, docMap)
    })

    // initialize state with docs from project
    const stepDocs: Dictionary<LoanDocument & File> = {} as unknown as Dictionary<LoanDocument & File>
    _step?.requiredDocuments?.forEach(slot => stepDocs[slot] = projectDocs[slot])

    // If limiting to specific slots, ensure those existing docs are included as well
    if (limitToSlots && limitToSlots.length > 0) {
      limitToSlots.forEach(slot => {
        if (!stepDocs[slot]) {
          stepDocs[slot] = projectDocs[slot]
        }
      })
    }

    setFormState({
      uploadType: _step?.uploadType,
      stepDocs
    })
  }, [project, isc, limitToSlots])

  const onSubmit = (stepDocs: Dictionary<LoanDocument & File>) => {
    setSubmitting(true)

    const documents = Object.entries(stepDocs)
      .filter(([slot]) => !limitToSlots || limitToSlots.includes(slot))
      .map(([, doc]) => doc)

    // CheckListItems has only one item
    const checklistItemId = checklistItems[0].id

    api.put(`/loanapps/${project.id}/docs/submit`, {
      ...formState,
      documents,
      checklistItemId
    }).then(proj => {
      const p = new StatusData(proj)
      p.uiSort()
      onProjectChange(p)
      const runs = p.activeAgentRuns()
      toast.success(runs.length > 0
        ? '🎉 Your documents have been received and are being automatically reviewed, stand by for an update momentarily...'
        : 'Documents uploaded successfully, you will be notified once they are reviewed', { autoClose: false })
    }).catch(ex => {
      const err = ex.error || ex.message || JSON.stringify(ex)
      toast.error(err, { autoClose: false })
    }).finally(() => setSubmitting(false))
  }

  const mapChecklistItemsToRequiredDocs = (checklistItems) => {
    return checklistItems.flat()
      .map(item => ({
        slot: item?.label,
        instructions: item?.description,
      }))
  }

  const requiredDocuments = (limitToSlots && limitToSlots.length > 0)
    ? mapChecklistItemsToRequiredDocs(checklistItems).filter(d => limitToSlots.includes(d.slot))
    : mapChecklistItemsToRequiredDocs(checklistItems)

  const onChangeImg = (doc, status, reqDoc) => {
    const stepDocs = {
      ...formState.stepDocs,
      [reqDoc.slot]: {
        ...doc,
        docType: reqDoc.slot,
        status: status || SolarDocStatus.PendingUpload,
        agentResult: ''
      }
    }

    setFormState({ ...formState, stepDocs })
    if (status === SolarDocStatus.Uploaded) {
      // Submit only the limited slots if provided
      const filteredStepDocs = limitToSlots && limitToSlots.length > 0
        ? Object.fromEntries(Object.entries(stepDocs).filter(([slot]) => limitToSlots.includes(slot)))
        : stepDocs
      onSubmit(filteredStepDocs as Dictionary<LoanDocument & File>)
    }
  }

  const onResponseToAgentImg = (doc, reqDoc) => {
    const path = `/loanapps/${project.id}/docs/${encodeURIComponent(step.uploadType)}/${encodeURIComponent(reqDoc.slot)}`
    api.patch(path, doc)
      .then(_proj => {
        const proj = new StatusData(_proj)
        proj.uiSort()
        onProjectChange(proj)
      })
      .catch(ex => toast.error(ex.error, { autoClose: false }))
  }

  return (
    <>
      {requiredDocuments.map(reqDoc => (
        <UploadSlot
          key={reqDoc.slot}
          doc={formState.stepDocs[reqDoc.slot]}
          slotConfig={reqDoc}
          project={project}
          onProjectChange={onProjectChange}
          onChange={(doc, status) => onChangeImg(doc, status, reqDoc)}
          onResponseToAgent={(doc) => onResponseToAgentImg(doc, reqDoc)}
          handleRowToggle={handleRowToggle}
          bankerComments={bankerComments}
          isCompleted={isCompleted}
          dateField={dateField}
          status={status}
          checklistOwner={checklistOwner}
        />
      ))}
    </>
  )
}

export default InstallerUploadForm
